import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { useToast } from "../hooks/use-toast";
import { Megaphone, Plus, Pencil, Trash2, Save, X, Clock, Eye, CalendarClock, CheckCircle2, Hourglass, PowerOff } from "lucide-react";
import { fmtMDT } from "../lib/time";
import FeaturedCard from "./FeaturedCard";

// Convert an ISO string (or empty) to the value expected by <input type="datetime-local">.
// datetime-local wants "YYYY-MM-DDTHH:mm" in *local* time.
const toDatetimeLocal = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
};
const fromDatetimeLocal = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d.toISOString();
};

const emptyForm = {
  label: "New Article",
  title: "",
  subtitle: "",
  button_enabled: true,
  cta: "Read Now",
  url: "",
  image: "",
  starts_at: "",
  ends_at: "",
  active: true,
  display_order: 100,
};

function statusOf(a) {
  if (!a.active) return { key: "disabled", label: "Disabled", cls: "bg-muted text-muted-foreground border-border", icon: PowerOff };
  const now = Date.now();
  const starts = a.starts_at ? new Date(a.starts_at).getTime() : null;
  const ends = a.ends_at ? new Date(a.ends_at).getTime() : null;
  if (starts && now < starts) return { key: "scheduled", label: "Scheduled", cls: "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/30", icon: Hourglass };
  if (ends && now > ends) return { key: "expired", label: "Expired", cls: "bg-muted text-muted-foreground border-border", icon: CalendarClock };
  return { key: "live", label: "Live", cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30", icon: CheckCircle2 };
}

// Coerce either a saved announcement doc or the in-progress editor form into
// the shape FeaturedCard expects. Titles fall back to a placeholder so the
// preview still renders while the admin is typing.
function toArticle(src, opts = {}) {
  const isForm = opts.isForm === true;
  const starts = isForm ? fromDatetimeLocal(src.starts_at) : src.starts_at;
  const ends = isForm ? fromDatetimeLocal(src.ends_at) : src.ends_at;
  return {
    id: src.id || "preview",
    label: src.label || "New Article",
    title: src.title?.trim() ? src.title : "Untitled announcement",
    subtitle: src.subtitle || "",
    cta: src.cta || "Read Now",
    url: src.url || "#",
    image: src.image || "",
    button_enabled: src.button_enabled !== false,
    starts_at: starts,
    ends_at: ends,
    active: src.active !== false,
  };
}

function PreviewStatusBar({ article }) {
  const st = statusOf(article);
  const Icon = st.icon;
  return (
    <div className="flex items-center gap-3 flex-wrap text-[11.5px]">
      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-semibold ${st.cls}`}>
        <Icon className="w-3 h-3" /> {st.label}
      </span>
      <span className="inline-flex items-center gap-1 text-muted-foreground">
        <Clock className="w-3 h-3" />
        {article.starts_at ? fmtMDT(article.starts_at) : "immediate"}
        {" → "}
        {article.ends_at ? fmtMDT(article.ends_at) : "no end"}
      </span>
    </div>
  );
}

function AnnouncementPreviewDialog({ article, onClose }) {
  if (!article) return null;
  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-5xl" data-testid="ann-preview-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <Eye className="w-4 h-4 text-primary" /> Dashboard preview
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <PreviewStatusBar article={article} />
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
            Exactly how the hero banner renders on the dashboard
          </div>
          <div className="rounded-xl border border-dashed border-border p-3 bg-muted/20">
            <div className="min-h-[220px]">
              <FeaturedCard article={article} />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AnnouncementPreview({ form }) {
  const article = toArticle(form, { isForm: true });
  return (
    <div className="space-y-2">
      <PreviewStatusBar article={article} />
      <div className="rounded-xl border border-dashed border-border p-2 bg-muted/20">
        <FeaturedCard article={article} />
      </div>
    </div>
  );
}

function AnnouncementEditorDialog({ item, onClose, onSaved }) {
  const { toast } = useToast();
  const isNew = !item?.id;
  const [form, setForm] = useState({ ...emptyForm });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (item?.id) {
      setForm({
        label: item.label || "New Article",
        title: item.title || "",
        subtitle: item.subtitle || "",
        button_enabled: item.button_enabled !== false,
        cta: item.cta || "Read Now",
        url: item.url || "",
        image: item.image || "",
        starts_at: toDatetimeLocal(item.starts_at),
        ends_at: toDatetimeLocal(item.ends_at),
        active: item.active !== false,
        display_order: item.display_order ?? 100,
      });
    } else {
      setForm({ ...emptyForm });
    }
  }, [item]);

  const patch = (p) => setForm((f) => ({ ...f, ...p }));

  const save = async () => {
    if (!form.title.trim()) {
      toast({ title: "Title is required", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      const payload = {
        label: form.label,
        title: form.title,
        subtitle: form.subtitle || null,
        button_enabled: form.button_enabled,
        cta: form.cta || null,
        url: form.url || null,
        image: form.image || null,
        starts_at: fromDatetimeLocal(form.starts_at),
        ends_at: fromDatetimeLocal(form.ends_at),
        active: form.active,
        display_order: Number(form.display_order) || 100,
      };
      if (isNew) {
        await api.post("/admin/announcements", payload);
      } else {
        await api.put(`/admin/announcements/${item.id}`, payload);
      }
      toast({ title: isNew ? "Announcement created" : "Announcement saved" });
      onSaved?.();
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-5xl">
        <DialogHeader><DialogTitle>{isNew ? "New announcement" : "Edit announcement"}</DialogTitle></DialogHeader>
        <div className="py-2 max-h-[75vh] overflow-y-auto tabler-scroll pr-1 space-y-5">
          {/* Live preview — full width, pinned above the form */}
          <div>
            <Label className="text-[12px] flex items-center gap-1 mb-2 text-muted-foreground"><Eye className="w-3.5 h-3.5" /> Live preview — dashboard fidelity</Label>
            <AnnouncementPreview form={form} />
          </div>

          {/* Form options below */}
          <div className="space-y-3">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div>
                <Label>Label (small caps eyebrow)</Label>
                <Input data-testid="ann-label" value={form.label} onChange={(e) => patch({ label: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>Title</Label>
                <Input data-testid="ann-title" value={form.title} onChange={(e) => patch({ title: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div>
              <Label>Subtitle / body</Label>
              <Textarea data-testid="ann-subtitle" value={form.subtitle} onChange={(e) => patch({ subtitle: e.target.value })} rows={3} className="mt-1" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div>
                <Label>Button text</Label>
                <Input data-testid="ann-cta" value={form.cta} onChange={(e) => patch({ cta: e.target.value })} placeholder="Read Now" className="mt-1" />
              </div>
              <div>
                <Label>Button URL</Label>
                <Input data-testid="ann-url" value={form.url} onChange={(e) => patch({ url: e.target.value })} placeholder="https://…" className="mt-1" />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <div className="font-medium text-[13px]">Show button</div>
                <div className="text-[11.5px] text-muted-foreground">Hide to render just the headline and body.</div>
              </div>
              <Switch checked={form.button_enabled} onCheckedChange={(v) => patch({ button_enabled: v })} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div>
                <Label>Starts at</Label>
                <Input type="datetime-local" data-testid="ann-starts" value={form.starts_at} onChange={(e) => patch({ starts_at: e.target.value })} className="mt-1" />
                <div className="text-[10.5px] text-muted-foreground mt-1">Leave blank for immediate.</div>
              </div>
              <div>
                <Label>Ends at</Label>
                <Input type="datetime-local" data-testid="ann-ends" value={form.ends_at} onChange={(e) => patch({ ends_at: e.target.value })} className="mt-1" />
                <div className="text-[10.5px] text-muted-foreground mt-1">Leave blank for no expiry.</div>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <div>
                  <div className="font-medium text-[13px]">Active</div>
                  <div className="text-[11.5px] text-muted-foreground">Uncheck to keep as draft.</div>
                </div>
                <Switch data-testid="ann-active" checked={form.active} onCheckedChange={(v) => patch({ active: v })} />
              </div>
              <div>
                <Label>Display order</Label>
                <Input type="number" value={form.display_order} onChange={(e) => patch({ display_order: e.target.value })} className="mt-1" />
                <div className="text-[10.5px] text-muted-foreground mt-1">Lower = higher priority.</div>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}><X className="w-4 h-4 mr-1" /> Cancel</Button>
          <Button data-testid="ann-save-btn" onClick={save} disabled={busy || !form.title.trim()} className="gap-1.5">
            <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AnnouncementsEditor() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/announcements");
      setItems(data?.announcements || []);
    } catch (e) {
      toast({ title: "Failed to load announcements", variant: "destructive" });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const del = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try {
      await api.delete(`/admin/announcements/${a.id}`);
      toast({ title: "Announcement deleted" });
      load();
    } catch (e) {
      toast({ title: "Delete failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
            <Megaphone className="w-4 h-4 text-primary" />
          </div>
          <div>
            <CardTitle>Featured announcements</CardTitle>
            <CardDescription>Schedule the hero banner on the dashboard. Multiple entries supported.</CardDescription>
          </div>
        </div>
        <Button data-testid="ann-new-btn" size="sm" className="gap-1.5" onClick={() => setEditing({})}>
          <Plus className="w-4 h-4" /> New announcement
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading ? (
          <div className="h-20 rounded-lg bg-muted/40 animate-pulse" />
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-6 text-center text-[13px] text-muted-foreground">
            No announcements yet. Create one to control the Featured banner on the dashboard.
          </div>
        ) : (
          items.map((a) => {
            const st = statusOf(a);
            const StIcon = st.icon;
            return (
              <div
                key={a.id}
                data-testid={`ann-row-${a.id}`}
                className="flex items-start justify-between gap-3 rounded-lg border border-border p-3 hover:bg-muted/30"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-semibold ${st.cls}`}>
                      <StIcon className="w-3 h-3" /> {st.label}
                    </span>
                    <span className="text-[10.5px] uppercase tracking-wide text-muted-foreground font-semibold">{a.label}</span>
                  </div>
                  <div className="mt-1 text-[14px] font-semibold truncate">{a.title}</div>
                  {a.subtitle && <div className="text-[12.5px] text-muted-foreground line-clamp-2">{a.subtitle}</div>}
                  <div className="mt-1 text-[11px] text-muted-foreground inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {a.starts_at ? fmtMDT(a.starts_at) : "immediate"} →{" "}
                    {a.ends_at ? fmtMDT(a.ends_at) : "no end"}
                    {a.updated_by && <span className="ml-2 opacity-70">· last edited by {a.updated_by}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setPreviewing(a)}
                    data-testid={`ann-preview-${a.id}`}
                    title="Preview on dashboard"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing(a)} data-testid={`ann-edit-${a.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => del(a)} data-testid={`ann-delete-${a.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </CardContent>

      {editing && (
        <AnnouncementEditorDialog
          item={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {previewing && (
        <AnnouncementPreviewDialog
          article={toArticle(previewing)}
          onClose={() => setPreviewing(null)}
        />
      )}
    </Card>
  );
}
